import { FC, useEffect, useState } from 'react';
import { useMutation, useQuery } from 'react-query';
import { BrandHubApi } from 'app/modules/brandHub/api/brandHub';
import { AiModelInfo } from 'app/modules/brandHub/types';
import TimeUtils from 'app/utils/time';
import { Button, Icon } from '@kontentino/ui';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import AiImageSuggestionsForm, {
  MAX_FILES,
  SelectOption,
  ModelOptionsGroup,
} from 'app/modules/aiContent/components/aiImageSuggestions/AiImageSuggestionsForm';
import FilesUtil from 'utils/files';
import AiContentRemainingAttemptsBadge from 'app/modules/aiContent/components/AiContentRemainingAttemptsBadge';
import { useTextEditorAiContent } from 'app/modules/textEditor/components/textEditorAiContent/TextEditorAiContentProvider';
import useSubscriptionInfo from 'app/hooks/useSubscriptionInfo';
import {
  IMAGE_COUNT_OPTIONS,
  ORIENTATION_OPTIONS,
  STYLE_OPTIONS,
  TYPE_OPTIONS,
} from 'app/modules/aiContent/constants/aiImageSuggestions';
import { useToast } from 'app/hooks/useToast';
import { ApiClientError } from 'api/client';
import Logger from 'utils/logger';
import Modal from 'components/shared/Modal';
import { useFileSource } from 'app/components/fileSourceInput/hooks/useFileSource';
import { MediaLibraryItem } from 'types/MediaLibrary';
import { GroupBase } from 'react-select';
import { faSparkles } from '@fortawesome/pro-regular-svg-icons/faSparkles';
import { queryKey } from 'constants/queryKey';

type Props = {
  onSubmitUpload: (files: File[]) => void | Promise<void>;
  page: {
    id: number;
    type: number;
  };
  initialReferenceImages?: string[];
  mode?: 'create' | 'edit';
};

const AiImageSuggestionsSettings: FC<Props> = ({
  onSubmitUpload,
  page,
  initialReferenceImages,
  mode = 'create',
}) => {
  const [description, setDescription] = useState<string>('');
  const [orientation, setOrientation] = useState<SelectOption<string>>(
    ORIENTATION_OPTIONS[page.type][0],
  );
  const [selectedStyle, setSelectedStyle] = useState<SelectOption<string>>(
    (STYLE_OPTIONS[0] as GroupBase<SelectOption<string>>).options.find(
      (option) => option?.value === 'realistic',
    ) || (STYLE_OPTIONS[0] as GroupBase<SelectOption<string>>).options[0],
  );
  const [selectedType, setSelectedType] = useState<SelectOption<string>>(
    TYPE_OPTIONS[0],
  );
  const [imageCount, setImageCount] = useState<SelectOption<number>>(
    IMAGE_COUNT_OPTIONS[1],
  );
  const [referenceImages, setReferenceImages] = useState<string[]>(
    initialReferenceImages ?? [],
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [images, setImages] = useState<string[]>([]);
  const [failedSlots, setFailedSlots] = useState<number[]>([]);
  const [lastGeneratePayload, setLastGeneratePayload] = useState<
    Parameters<typeof BrandHubApi.generateImage>[0] | null
  >(null);
  const [selectedModel, setSelectedModel] =
    useState<SelectOption<string>>(null);
  const { queries } = useTextEditorAiContent();

  const { t } = useTranslation();

  const aiImageModelsQuery = useQuery(
    queryKey.aiModels('image'),
    () => BrandHubApi.getAiModels('image'),
    {
      staleTime: TimeUtils.calcMilliseconds.minutes(5),
    },
  );

  useEffect(() => {
    const imageData = aiImageModelsQuery.data?.image;
    const featuredModels = imageData?.featured || [];
    if (!selectedModel && featuredModels.length > 0) {
      const firstModel = featuredModels[0];
      setSelectedModel({ value: firstModel.id, label: firstModel.name });
    }
  }, [aiImageModelsQuery.data, selectedModel]);

  const createGroupedModelOptions = (
    data: { featured?: AiModelInfo[]; all?: AiModelInfo[] } | undefined,
  ): ModelOptionsGroup[] => {
    if (!data) return [];
    return [
      {
        label: t('aiModels.top', 'Featured'),
        options: (data.featured || []).map((model) => ({
          value: model.id,
          label: model.name,
        })),
      },
      {
        label: t('aiModels.newest', 'All Models'),
        options: (data.all || []).map((model) => ({
          value: model.id,
          label: model.name,
        })),
      },
    ].filter((group) => group.options.length > 0);
  };

  const imageModelOptions = createGroupedModelOptions(
    aiImageModelsQuery.data?.image,
  );
  const { accountPlanType } = useSubscriptionInfo();
  const toast = useToast();

  const fileSource = useFileSource({
    page,
    onChange: (mediaItems: MediaLibraryItem[]) => {
      const urls = mediaItems.map((item) => item.data.original.src);
      setReferenceImages((prev) => [...prev, ...urls]);
    },
    maxFiles: MAX_FILES - referenceImages.length,
    allowedFiles: ['image'],
  });

  const buildImageError = (err: ApiClientError) => {
    Logger.error(err);
    let errorMessage = err.userMessage ?? t('somethingWentWrong');
    if (
      err.message?.includes('Request entity too large') ||
      err.message?.includes('payload too large')
    ) {
      errorMessage = t('aiImageSuggestions.errors.requestTooLarge');
    } else if (err.message?.includes('safety system')) {
      errorMessage = t('aiImageSuggestions.errors.safetySystemRejection');
    }
    return errorMessage;
  };

  const generateMutation = useMutation(BrandHubApi.generateImage, {
    onSuccess(data) {
      const ordered = [...data].sort((a, b) => a.slotIndex - b.slotIndex);
      const okImages = ordered
        .filter((item) => item.status === 'ok' && item.image)
        .map((item) => `data:image/png;base64,${item.image}`);
      const failed = ordered
        .filter((item) => item.status === 'failed')
        .map((item) => item.slotIndex);
      setImages(okImages);
      setFailedSlots(failed);
      setReferenceImages([]);
    },
    onError(err: ApiClientError) {
      toast(buildImageError(err), 'error');
    },
  });

  const retryMutation = useMutation(BrandHubApi.retryImage, {
    onSuccess(data) {
      const okNew = data.filter((item) => item.status === 'ok' && item.image);
      const stillFailed = data
        .filter((item) => item.status === 'failed')
        .map((item) => item.slotIndex);
      setImages((prev) => [
        ...prev,
        ...okNew.map((item) => `data:image/png;base64,${item.image}`),
      ]);
      const retriedSlots = data.map((item) => item.slotIndex);
      setFailedSlots((prev) => [
        ...prev.filter((slot) => !retriedSlots.includes(slot)),
        ...stillFailed,
      ]);
    },
    onError(err: ApiClientError) {
      toast(buildImageError(err), 'error');
    },
  });

  const handleRetryFailed = () => {
    if (!lastGeneratePayload || failedSlots.length === 0) return;
    const idempotencyKey =
      typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    retryMutation.mutate({
      style: lastGeneratePayload.style,
      prompt: lastGeneratePayload.prompt,
      aspectRatio: lastGeneratePayload.aspectRatio,
      planType: lastGeneratePayload.planType,
      type: lastGeneratePayload.type,
      referenceImages: lastGeneratePayload.referenceImages,
      model: lastGeneratePayload.model,
      slotIndexes: failedSlots,
      idempotencyKey,
    });
  };

  const isEditMode = mode === 'edit';

  const handleSubmit = () => {
    if (!isEditMode && (!selectedStyle || !imageCount || !selectedType)) return;
    if (!orientation) return;

    setImages([]);
    setFailedSlots([]);

    const payload = {
      prompt: description,
      style: isEditMode ? 'preserve' : selectedStyle!.value,
      type: isEditMode ? 'image_edit' : selectedType!.value,
      count: isEditMode ? 1 : imageCount!.value,
      aspectRatio:
        orientation?.value || ORIENTATION_OPTIONS[page.type][0].value,
      planType: accountPlanType,
      referenceImages: [...referenceImages, ...images],
      model: selectedModel?.value,
    };
    setLastGeneratePayload(payload);
    generateMutation.mutate(payload);
  };

  const handleUseInPost = async (imageUrls: string[]) => {
    setIsSubmitting(true);
    try {
      const imageFiles = await Promise.all(
        imageUrls.map((url) => FilesUtil.base64ToPngFile(url)),
      );
      await onSubmitUpload(imageFiles);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRemixImage = (imageUrl: string | string[]) => {
    const urls = Array.isArray(imageUrl) ? imageUrl : [imageUrl];
    setReferenceImages(urls);
    setImages([]);
  };

  const handleRemoveResultImage = (imageUrl: string) => {
    setImages((prev) => prev.filter((url) => url !== imageUrl));
  };

  return (
    <>
      <Modal.Header
        className="tw-flex tw-items-center tw-justify-between tw-p-4 tw-pb-0"
        childrenClassName="tw-w-full tw-mt-0 tw-mr-8"
      >
        <div className="tw-mb-2 tw-flex tw-w-full tw-items-center tw-gap-2 !tw-font-regular">
          <Icon icon={faSparkles} size="xl" className="tw-text-primary-100" />
          <span className="tw-text-xl tw-text-grayscale-180">
            {isEditMode
              ? t('aiImageSuggestions.editTitle', {
                  defaultValue: 'Edit image',
                })
              : t('aiImageSuggestions.createTitle')}
          </span>
          <div className="tw-ml-auto">
            <AiContentRemainingAttemptsBadge options={queries.options} />
          </div>
        </div>
      </Modal.Header>
      <Modal.Content className="tw-px-0">
        <div
          className={clsx(
            'tw-relative tw-flex tw-w-full tw-flex-col tw-gap-6 tw-overflow-visible',
          )}
          data-name="ai-content-generate-text"
        >
          <AiImageSuggestionsForm
            mode={mode}
            fileSource={fileSource}
            page={page}
            description={description}
            selectedStyle={selectedStyle}
            selectedType={selectedType}
            imageCount={imageCount}
            onDescriptionChange={setDescription}
            onStyleChange={setSelectedStyle}
            onTypeChange={setSelectedType}
            onImageCountChange={setImageCount}
            onOrientationChange={setOrientation}
            orientation={orientation}
            referenceImages={referenceImages}
            resultImages={images}
            onReferenceImagesChange={setReferenceImages}
            onUseInPost={handleUseInPost}
            onRemixImage={handleRemixImage}
            onRemoveResultImage={handleRemoveResultImage}
            isLoading={generateMutation.isLoading}
            isSubmitting={isSubmitting}
            handleSubmit={handleSubmit}
            selectedModel={selectedModel}
            onModelChange={setSelectedModel}
            modelOptions={imageModelOptions}
            isModelLoading={aiImageModelsQuery.isLoading}
          />
          {failedSlots.length > 0 && (
            <div
              className="tw-border-warning-60 tw-mx-4 tw-flex tw-items-center tw-justify-between tw-gap-3 tw-rounded-lg tw-border tw-bg-warning-10 tw-p-3 tw-text-sm"
              data-cy="ai-image-suggestions_retry-failed-bar"
            >
              <span className="tw-text-grayscale-180">
                {t('aiImageSuggestions.failedSlots', {
                  count: failedSlots.length,
                  defaultValue: '{{count}} image(s) failed to generate.',
                })}
              </span>
              <Button
                size="small"
                variant="primary"
                onClick={handleRetryFailed}
                disabled={retryMutation.isLoading}
                data-cy="ai-image-suggestions_retry-failed-button"
              >
                {retryMutation.isLoading
                  ? t('common.loading', { defaultValue: 'Loading…' })
                  : t('aiImageSuggestions.retryFailed', {
                      defaultValue: 'Retry failed',
                    })}
              </Button>
            </div>
          )}
        </div>
      </Modal.Content>
    </>
  );
};

export default AiImageSuggestionsSettings;
